"use client";
import Meet from "@client/components/Meet";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { Logger } from "@shared/utils/logger";
import { Spinner } from "@client/components/ui/Spinner";
import { toastNotify } from "@client/components/ui/toast";
import { useSetupE2EE } from "@client/utils/useSetupE2EE";
import { useAppDispatch } from "@client/store/store.hooks";
import { decodePassphrase } from "@client/utils/client-utils";
import { LocalUserChoices } from "@livekit/components-react";
import type { ConnectionDetails } from "@client/types/connection.types";
import { useLowCPUOptimizer } from "@client/utils/usePerformanceOptimizer";
import ConnectionErrorOverlay from "@client/components/meet/ConnectionErrorOverlay";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { setMeetingDetailsRedux, clearMeetingDetailsRedux } from "@client/store/slices/meeting.slice";
import { setConnectionDetailsRedux } from "@client/store/slices/connection.slice";
import {
  Room,
  RoomEvent,
  VideoCodec,
  RoomOptions,
  VideoPresets,
  DisconnectReason,
  ConnectionState as LiveKitConnectionState,
  RoomConnectOptions,
  VideoCaptureOptions,
  TrackPublishDefaults,
  DeviceUnsupportedError,
  ExternalE2EEKeyProvider
} from "livekit-client";

/** Configuration properties for the `VideoConference` component. */
interface VideoConferenceProps {
  userChoices: LocalUserChoices;
  connectionDetails: ConnectionDetails;
  options: {
    hq: boolean;
    codec: VideoCodec;
  };
  isHost: boolean;
  startPresenting?: boolean;
  /** Called just before the leave sequence navigates away (e.g. emit leave_call to socket server). */
  onBeforeLeave?: () => void;
  onBreakoutConnectionChange?: (details: {
    roomName: string;
    participantToken: string;
    chatId?: string | null;
    chatEnabled?: boolean;
    chatEmbedUrl?: string | null;
    breakoutSessionId?: string;
    breakoutRoomId?: string;
    breakoutTitle?: string;
    breakoutThemeColor?: string;
    guestToken?: string | null;
    isGuest?: boolean;
    guestId?: string;
  }) => void;
}

/** Tracks active room connection status. */
interface RoomConnectionUIState {
  isConnecting: boolean;
  error: string | null;
  isKicked?: boolean;
}

interface UseRoomConnectionStateReturn extends RoomConnectionUIState {
  setConnectionError: (errorMessage: string, isKicked?: boolean) => void;
}

const CONNECTION_TIMEOUT_MS = 2000;
const DISCONNECT_ERROR_GRACE_MS = 5000;
const DEFAULT_VIDEO_CODEC: VideoCodec = "vp9";

// Error patterns that should be silently ignored (user cancellations, permissions)
const IGNORABLE_ERROR_PATTERNS = [
  "not allowed",
  "permission denied",
  "user agent",
  "platform",
  "aborted",
  "cancelled",
  "canceled",
  "denied by user"
] as const;

/**
 * Manages room connection lifecycle (Connect, Disconnect, Reconnect).
 * Suppresses errors for intentional leaves.
 */
const useRoomConnectionState = (room: Room, isIntentionalLeave?: RefObject<boolean>): UseRoomConnectionStateReturn => {
  const t = useTranslations();
  const translationRef = useRef(t);
  useEffect(() => {
    translationRef.current = t;
  }, [t]);
  const disconnectErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<RoomConnectionUIState>({
    isConnecting: true,
    error: null,
    isKicked: false
  });

  const setConnectionError = useCallback((errorMessage: string, isKicked = false) => {
    setState((prev) => ({ ...prev, isConnecting: false, error: errorMessage, isKicked }));
  }, []);

  const clearDisconnectErrorTimer = useCallback(() => {
    if (disconnectErrorTimerRef.current) {
      clearTimeout(disconnectErrorTimerRef.current);
      disconnectErrorTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    setState({ isConnecting: true, error: null, isKicked: false });
    /** Handler for successful room connection */
    const handleConnected = () => {
      clearDisconnectErrorTimer();
      if (isIntentionalLeave) isIntentionalLeave.current = false;
      setState((prev) => ({ ...prev, isConnecting: false, error: null, isKicked: false }));
    };

    /** Handler for room disconnection */
    const handleDisconnected = (reason?: DisconnectReason) => {
      clearDisconnectErrorTimer();

      // Don't show error if leave was intentional
      if (isIntentionalLeave?.current) {
        setState((prev) => ({ ...prev, isConnecting: false, error: null, isKicked: false }));
        return;
      }

      if (reason === DisconnectReason.CLIENT_INITIATED) {
        setState((prev) => ({ ...prev, isConnecting: false, error: null, isKicked: false }));
        return;
      }

      let errorMessage: string = translationRef.current("errors.connectionLost");
      let isKicked = false;
      if (reason === DisconnectReason.PARTICIPANT_REMOVED) {
        errorMessage = translationRef.current("participant.remove");
        isKicked = true;
        setState((prev) => ({ ...prev, isConnecting: false, error: errorMessage, isKicked }));
        toastNotify.error(`${translationRef.current("common.error")}: ${errorMessage}`);
        return;
      }

      // Background tab / ICE blips: LiveKit may disconnect (freeze) then reconnect — wait before fatal UI.
      setState((prev) => ({ ...prev, isConnecting: true, error: null, isKicked: false }));
      disconnectErrorTimerRef.current = setTimeout(() => {
        if (
          room.state === LiveKitConnectionState.Connected ||
          room.state === LiveKitConnectionState.Connecting ||
          room.state === LiveKitConnectionState.Reconnecting
        ) {
          return;
        }
        toastNotify.error(`${translationRef.current("common.error")}: ${errorMessage}`);
        setState((prev) => ({ ...prev, isConnecting: false, error: errorMessage, isKicked: false }));
      }, DISCONNECT_ERROR_GRACE_MS);
    };

    /** Handler for room reconnection attempt */
    const handleReconnecting = () => {
      clearDisconnectErrorTimer();
      setState((prev) => ({ ...prev, isConnecting: true, error: null, isKicked: false }));
    };

    const handleReconnected = () => {
      clearDisconnectErrorTimer();
      setState((prev) => ({ ...prev, isConnecting: false, error: null, isKicked: false }));
    };

    /** Handler for track subscription - indicates successful media streaming */
    const handleTrackSubscribed = () => {
      setState((prev) => ({ ...prev, isConnecting: false }));
    };

    // Register event listeners
    room.on(RoomEvent.Connected, handleConnected);
    room.on(RoomEvent.Disconnected, handleDisconnected);
    room.on(RoomEvent.Reconnecting, handleReconnecting);
    room.on(RoomEvent.Reconnected, handleReconnected);
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed);

    // Check if already connected on mount
    if (room.state === "connected") {
      setState((prev) => ({ ...prev, isConnecting: false, error: null }));
    }

    // Safety timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      setState((prev) => ({ ...prev, isConnecting: false }));
    }, CONNECTION_TIMEOUT_MS);

    // Cleanup
    return () => {
      clearTimeout(timeout);
      clearDisconnectErrorTimer();
      room.off(RoomEvent.Connected, handleConnected);
      room.off(RoomEvent.Disconnected, handleDisconnected);
      room.off(RoomEvent.Reconnecting, handleReconnecting);
      room.off(RoomEvent.Reconnected, handleReconnected);
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed);
    };
  }, [room, isIntentionalLeave, clearDisconnectErrorTimer]);

  return {
    ...state,
    setConnectionError
  };
};

/** Configures LiveKit `RoomOptions` (codecs, adaptive stream, E2EE). */
const buildRoomOptions = (
  userChoices: LocalUserChoices,
  options: VideoConferenceProps["options"],
  e2eeEnabled: boolean,
  keyProvider: ExternalE2EEKeyProvider,
  worker: Worker | undefined
): RoomOptions => {
  // Fallback for E2EE compatibility (AV1/VP9 issues).
  let videoCodec: VideoCodec | undefined = options.codec || DEFAULT_VIDEO_CODEC;
  if (e2eeEnabled && (videoCodec === "av1" || videoCodec === "vp9")) {
    videoCodec = undefined;
  }

  const videoCaptureDefaults: VideoCaptureOptions = {
    deviceId: userChoices?.videoDeviceId ?? undefined,
    resolution: options.hq ? VideoPresets.h2160 : VideoPresets.h720
  };

  const publishDefaults: TrackPublishDefaults = {
    dtx: false,
    videoSimulcastLayers: options.hq ? [VideoPresets.h1080, VideoPresets.h720] : [VideoPresets.h540, VideoPresets.h216],
    red: !e2eeEnabled,
    videoCodec
  };

  return {
    videoCaptureDefaults,
    publishDefaults,
    audioCaptureDefaults: {
      deviceId: userChoices?.audioDeviceId ?? undefined
    },
    adaptiveStream: true,
    dynacast: true,
    // Keep call alive when switching browser tabs; LiveKit still listens for freeze/page lifecycle.
    disconnectOnPageLeave: false,
    e2ee: keyProvider && worker && e2eeEnabled ? { keyProvider, worker } : undefined
  };
};

/** VideoConference - Session Manager. */
const VideoConference = ({
  userChoices,
  connectionDetails,
  options,
  isHost,
  startPresenting,
  onBeforeLeave,
  onBreakoutConnectionChange: _onBreakoutConnectionChange
}: VideoConferenceProps) => {
  const t = useTranslations();
  const translationRef = useRef(t);
  useEffect(() => {
    translationRef.current = t;
  }, [t]);
  // Navigation & state
  const router = useRouter();
  const dispatch = useAppDispatch();
  const isIntentionalLeave = useRef(false);
  const [activeConnectionDetails, setActiveConnectionDetails] = useState(connectionDetails);

  const handleIntentionalLeave = useCallback(() => {
    isIntentionalLeave.current = true;
    onBeforeLeave?.();
  }, [onBeforeLeave]);

  // If no E2EE, mark as complete immediately
  const keyProvider = useMemo(() => new ExternalE2EEKeyProvider(), []);
  const { worker, e2eePassphrase } = useSetupE2EE();
  const e2eeEnabled = !!(e2eePassphrase && worker);
  const [e2eeSetupComplete, setE2eeSetupComplete] = useState(!e2eeEnabled);

  /** Filters out benign errors (cancellations, permissions) to prevent toast spam. */
  const handleError = useCallback((error: Error) => {
    const lowered = error.message.toLowerCase();
    const isIgnorable =
      error.name.toLowerCase() === "notallowederror" ||
      error.name.toLowerCase() === "aborterror" ||
      IGNORABLE_ERROR_PATTERNS.some((p) => lowered.includes(p));

    // Only notify if it's a genuine unexpected error, not a user action (like denying perms)
    if (!isIgnorable) {
      Logger.error("VideoConference", "Room error:", error);
      toastNotify.error(`${translationRef.current("common.error")}: ${error.message}`);
    }
  }, []);

  /** Handles encryption errors. */
  const handleEncryptionError = useCallback((error: Error) => {
    Logger.error("VideoConference", "Encryption error:", error);
    toastNotify.error(`${translationRef.current("common.error")}: ${error.message}`);
  }, []);

  const serverUrl = activeConnectionDetails.serverUrl;
  const participantToken = activeConnectionDetails.participantToken;
  const livekitRoomName = activeConnectionDetails.livekitRoomName ?? activeConnectionDetails.roomName;

  const livekitSessionKey = `${serverUrl}|${participantToken}|${livekitRoomName}`;
  const previousRoomRef = useRef<Room | null>(null);
  const connectGenerationRef = useRef(0);

  const room = useMemo(() => {
    const previous = previousRoomRef.current;
    if (previous) {
      isIntentionalLeave.current = true;
      void previous.disconnect().finally(() => {
        isIntentionalLeave.current = false;
      });
    }
    const nextRoom = new Room(buildRoomOptions(userChoices, options, e2eeEnabled, keyProvider, worker));
    previousRoomRef.current = nextRoom;
    return nextRoom;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one Room per LiveKit session
  }, [livekitSessionKey, e2eeSetupComplete]);

  const handleBreakoutConnectionChange = useCallback(
    (details: Parameters<NonNullable<VideoConferenceProps["onBreakoutConnectionChange"]>>[0]) => {
      isIntentionalLeave.current = true;
      void room.disconnect();
      const enteringBreakout = Boolean(details.breakoutRoomId);
      const updatedConnectionDetails: ConnectionDetails = {
        ...activeConnectionDetails,
        livekitRoomName: enteringBreakout
          ? details.roomName
          : details.roomName !== activeConnectionDetails.roomName
            ? details.roomName
            : undefined,
        participantToken: details.participantToken,
        chatId: details.chatId ?? null,
        chatEnabled: details.chatEnabled === true,
        chatEmbedUrl: details.chatEmbedUrl ?? null,
        breakoutSessionId: enteringBreakout ? details.breakoutSessionId : undefined,
        breakoutRoomId: enteringBreakout ? details.breakoutRoomId : undefined,
        breakoutTitle: enteringBreakout ? details.breakoutTitle : undefined,
        breakoutThemeColor: enteringBreakout ? details.breakoutThemeColor : undefined,
        guestToken: enteringBreakout
          ? (details.guestToken ?? activeConnectionDetails.guestToken ?? null)
          : (details.guestToken ?? null),
        isGuest: details.isGuest ?? activeConnectionDetails.isGuest,
        guestId: details.guestId ?? activeConnectionDetails.guestId
      };
      setActiveConnectionDetails(updatedConnectionDetails);
      dispatch(setConnectionDetailsRedux(updatedConnectionDetails));
    },
    [activeConnectionDetails, dispatch, room]
  );

  const connectOptions = useMemo(
    (): RoomConnectOptions => ({
      autoSubscribe: true
    }),
    []
  );

  const {
    isConnecting,
    error: connectionError,
    isKicked,
    setConnectionError
  } = useRoomConnectionState(room, isIntentionalLeave);

  // Sync meeting details to Redux.
  useEffect(() => {
    dispatch(
      setMeetingDetailsRedux({
        roomId: connectionDetails.roomId,
        roomName: connectionDetails.roomName,
        e2eeEnabled,
        e2eePassphrase: e2eePassphrase ?? null,
        codec: options.codec ?? null,
        hqEnabled: options.hq
      })
    );

    return () => {
      dispatch(clearMeetingDetailsRedux());
    };
  }, [
    dispatch,
    connectionDetails.roomId,
    connectionDetails.roomName,
    e2eeEnabled,
    e2eePassphrase,
    options.codec,
    options.hq
  ]);

  // Initialize E2EE key before connection.
  useEffect(() => {
    if (!e2eeEnabled) return;

    const setupE2EE = async () => {
      try {
        const decodedPassphrase = decodePassphrase(e2eePassphrase!);
        await keyProvider.setKey(decodedPassphrase);
        setE2eeSetupComplete(true);
      } catch (e) {
        if (e instanceof DeviceUnsupportedError) {
          toastNotify.warning(translationRef.current("toast.warning.e2eeUnsupported"));
          Logger.error("VideoConference", "E2EE unsupported device:", e);
        } else {
          // Re-throw critical errors to let the error boundary or connection logic handle them
          throw e;
        }
      }
    };

    setupE2EE();
  }, [e2eeEnabled, e2eePassphrase, keyProvider]);

  // Connect to LiveKit server.
  useEffect(() => {
    room.on(RoomEvent.EncryptionError, handleEncryptionError);
    room.on(RoomEvent.MediaDevicesError, handleError);

    if (!e2eeSetupComplete) {
      return () => {
        room.off(RoomEvent.EncryptionError, handleEncryptionError);
        room.off(RoomEvent.MediaDevicesError, handleError);
      };
    }

    const generation = ++connectGenerationRef.current;

    const connectToRoom = async () => {
      if (
        room.state === LiveKitConnectionState.Connected ||
        room.state === LiveKitConnectionState.Connecting ||
        room.state === LiveKitConnectionState.Reconnecting
      ) {
        return;
      }

      try {
        await room.connect(serverUrl, participantToken, connectOptions);

        if (generation !== connectGenerationRef.current) {
          return;
        }

        if (e2eeEnabled) {
          await room.setE2EEEnabled(true);
        }

        if (userChoices?.videoEnabled) {
          try {
            await room.localParticipant.setCameraEnabled(true);
          } catch (videoError) {
            const errorMsg = videoError instanceof Error ? videoError.message : String(videoError);
            if (
              !errorMsg.includes("not allowed") &&
              !errorMsg.includes("NotAllowedError") &&
              !errorMsg.includes("Permission denied")
            ) {
              Logger.warn("VideoConference", "Could not enable camera:", videoError);
            }
          }
        }
        if (userChoices?.audioEnabled) {
          try {
            await room.localParticipant.setMicrophoneEnabled(true);
          } catch (audioError) {
            const errorMsg = audioError instanceof Error ? audioError.message : String(audioError);
            if (
              !errorMsg.includes("not allowed") &&
              !errorMsg.includes("NotAllowedError") &&
              !errorMsg.includes("Permission denied")
            ) {
              Logger.warn("VideoConference", "Could not enable microphone:", audioError);
            }
          }
        }
      } catch (error) {
        if (generation !== connectGenerationRef.current) {
          return;
        }
        Logger.error("VideoConference", "Room connection failed:", error);
        const errorMessage =
          error instanceof Error && error.message ? error.message : translationRef.current("errors.failedToConnect");
        setConnectionError(errorMessage);
        handleError(error as Error);
      }
    };

    void connectToRoom();

    return () => {
      connectGenerationRef.current += 1;
      room.off(RoomEvent.EncryptionError, handleEncryptionError);
      room.off(RoomEvent.MediaDevicesError, handleError);
      if (room.state !== "disconnected") {
        isIntentionalLeave.current = true;
        void room.disconnect().finally(() => {
          isIntentionalLeave.current = false;
        });
      }
    };
  }, [
    e2eeSetupComplete,
    e2eeEnabled,
    room,
    serverUrl,
    participantToken,
    userChoices?.videoEnabled,
    userChoices?.audioEnabled,
    connectOptions,
    handleError,
    handleEncryptionError,
    setConnectionError
  ]);

  // Chrome may freeze background tabs and LiveKit disconnects; reconnect when the tab is focused again.
  useEffect(() => {
    if (!e2eeSetupComplete) return;

    const resumeAfterTabVisible = () => {
      if (document.hidden || isIntentionalLeave.current) return;
      if (room.state !== LiveKitConnectionState.Disconnected) return;

      const generation = ++connectGenerationRef.current;
      void (async () => {
        try {
          await room.connect(serverUrl, participantToken, connectOptions);
          if (generation !== connectGenerationRef.current) return;
          if (e2eeEnabled) {
            await room.setE2EEEnabled(true);
          }
        } catch (error) {
          Logger.warn("VideoConference", "Reconnect after returning to tab failed:", error);
        }
      })();
    };

    document.addEventListener("visibilitychange", resumeAfterTabVisible);
    return () => document.removeEventListener("visibilitychange", resumeAfterTabVisible);
  }, [e2eeSetupComplete, e2eeEnabled, room, serverUrl, participantToken, connectOptions]);

  /**
   * Performance
   * Low CPU mode for constrained devices
   */
  const lowPowerMode = useLowCPUOptimizer(room);

  useEffect(() => {
    if (lowPowerMode) {
      Logger.warn("VideoConference", "Low power mode enabled");
    }
  }, [lowPowerMode]);

  return (
    <div className="h-screen w-screen overflow-hidden bg-gray-900">
      {/* Loading Overlay */}
      {isConnecting && (
        <div className="absolute inset-0 bg-gray-900 z-50 flex flex-col items-center justify-center">
          <Spinner size="md" message={e2eeSetupComplete ? t("meeting.joining") : t("meeting.encrypting")} />
          <p className="text-white/60 text-sm mt-4">{t("meeting.connectingSubtext")}</p>
        </div>
      )}

      {/* Connection Error Overlay */}
      {connectionError && (
        <ConnectionErrorOverlay
          error={connectionError}
          isKicked={isKicked}
          onRetry={() => window.location.reload()}
          onHome={() => router.push("/")}
        />
      )}

      {/* Main Meeting Component - only render when no connection error */}
      {!connectionError && (
        <Meet
          room={room}
          userChoices={userChoices}
          isHost={isHost}
          connectionDetails={activeConnectionDetails}
          startPresenting={startPresenting}
          onIntentionalLeave={handleIntentionalLeave}
          onBreakoutConnectionChange={handleBreakoutConnectionChange}
        />
      )}
    </div>
  );
};

export default VideoConference;
